import {css} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';

import {NODE_SECTIONS_STYLES, noDragClass} from '../BenchlingNodeDesignConstants';
import {NodeSection} from '../BenchlingNodeTypes';

function BenchlingNodeSectionName(props: {sectionName: string}): JSX.Element {
  return (
    <div
      className={css(NODE_SECTIONS_STYLES.sectionName)}
      {...getTestProps({
        component: 'BenchlingNodeSections',
        element: 'sectionName',
      })}
    >
      {props.sectionName}
    </div>
  );
}

function BenchlingNodeSectionRow<TRowData>(props: {
  rowData: TRowData;
  rowRenderer: (rowData: TRowData) => JSX.Element | null;
}): JSX.Element {
  return (
    <div
      {...getTestProps({
        component: 'BenchlingNodeSections',
        element: 'sectionRow',
      })}
    >
      {props.rowRenderer(props.rowData)}
    </div>
  );
}

/**
 * Renders all sections and their rows for a BenchlingNode.
 *
 * Uses the `rowRenderer` provided in the node's data to render each row within a section.
 * Section names are displayed above their rows.
 *
 * This component is designed to be used as part of `BenchlingNode`, but can be used
 * independently when constructing custom nodes that follow the Benchling node structure.
 * Note: Requires `props.data.rowRenderer` to be defined.
 *
 * @example
 * // Basic usage within a custom React Flow node component:
 * import { BenchlingNodeSections } from './sub-components/BenchlingNodeSections';
 * import { NodeProps } from 'reactflow';
 * import { BenchlingNodeData, BenchlingNodeProps, DefaultRowData } from '../BenchlingNodeTypes';
 *
 * // Define your custom node data structure if needed, extending BenchlingNodeData
 * interface MyCustomNodeData extends BenchlingNodeData<DefaultRowData> {
 *   customField?: string;
 *   // Ensure rowRenderer is provided if using BenchlingNodeSections directly
 *   rowRenderer: (rowData: DefaultRowData) => JSX.Element;
 * }
 *
 * // Define the props for your custom node component
 * type MyCustomNodeProps = NodeProps<MyCustomNodeData>;
 *
 * function MyCustomNode(props: MyCustomNodeProps) {
 *   // Cast or construct the specific props needed
 *   const sectionsProps: BenchlingNodeProps<DefaultRowData, MyCustomNodeData> = props;
 *
 *   return (
 *     <div className="my-custom-node-wrapper">
 *       { Header and Summaries would go here  }
 *       <BenchlingNodeSections {...sectionsProps} />
 *     </div>
 *   );
 * }
 */
export function BenchlingNodeSections<TRowData>(props: {
  data: {
    sections: Array<NodeSection<TRowData>>;
    rowRenderer: (rowData: TRowData) => JSX.Element | null;
  };
}): JSX.Element {
  return (
    <div
      className={noDragClass(css(NODE_SECTIONS_STYLES.sections))}
      {...getTestProps({
        component: 'BenchlingNodeSections',
        element: 'root',
      })}
    >
      {props.data.sections
        .filter((section: NodeSection<TRowData>) => !section.hideSection)
        .map((section: NodeSection<TRowData>, index: number) => (
          <div
            key={`${section.sectionName}-${index}`}
            className={css(
              NODE_SECTIONS_STYLES.section,
              index !== 0 && !section.hideSection && NODE_SECTIONS_STYLES.sectionBorder,
              !section.hideSection && NODE_SECTIONS_STYLES.sectionVisible
            )}
            {...getTestProps({
              component: 'BenchlingNodeSections',
              element: 'section',
              key: section.sectionName,
            })}
          >
            <BenchlingNodeSectionName sectionName={section.sectionName} />
            <div className={css(NODE_SECTIONS_STYLES.sectionRows)}>
              {section.sectionRows.map((row: TRowData, rowIndex: number) => (
                <BenchlingNodeSectionRow
                  key={`${section.sectionName}-row-${rowIndex}`}
                  rowData={row}
                  rowRenderer={props.data.rowRenderer}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
